import { useEffect, useState, type FormEvent } from 'react'
import { useForm, usePage } from '@inertiajs/react'
import { Camera, Loader2, Trash2 } from 'lucide-react'
import { Avatar } from '@/components/design-system'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { SharedProps } from '@/frontend/types/inertia'

export interface ProfileSettingsCopy {
  title: string
  description: string
  name: string
  name_placeholder: string
  name_hint: string
  email: string
  email_hint: string
  photo: string
  photo_hint: string
  choose_photo: string
  change_photo: string
  remove_photo: string
  save: string
  saving: string
}

export function AccountProfileSection({ copy }: { copy: ProfileSettingsCopy }) {
  const user = usePage<SharedProps>().props.auth.user
  const [preview, setPreview] = useState<string | null>(user?.avatar_url || null)
  const form = useForm({
    profile: {
      name: user?.name || '',
      profile_image: null as File | null,
      remove_profile_image: false as boolean,
    },
  })

  useEffect(() => {
    const file = form.data.profile.profile_image
    if (!file) {
      setPreview(form.data.profile.remove_profile_image ? null : user?.avatar_url || null)
      return
    }

    const objectUrl = URL.createObjectURL(file)
    setPreview(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [form.data.profile.profile_image, form.data.profile.remove_profile_image, user?.avatar_url])

  function selectImage(file: File | null) {
    form.setData('profile', { ...form.data.profile, profile_image: file, remove_profile_image: false })
  }

  function submit(event: FormEvent) {
    event.preventDefault()
    form.patch('/dashboard/profile', {
      forceFormData: true,
      preserveScroll: true,
      onSuccess: () => form.setData('profile', {
        ...form.data.profile,
        profile_image: null,
        remove_profile_image: false,
      }),
    })
  }

  if (!user) return null

  return <Card data-testid="account-profile-settings">
    <CardHeader className="border-b border-border">
      <CardTitle><h2 className="text-lg font-semibold">{copy.title}</h2></CardTitle>
      <p className="text-sm text-muted-foreground">{copy.description}</p>
    </CardHeader>
    <CardContent className="pt-6">
      <form onSubmit={submit} className="grid gap-6 md:grid-cols-[220px_minmax(0,1fr)]">
        <div>
          <Label>{copy.photo}</Label>
          <div className="mt-3 flex items-center gap-4 md:flex-col md:items-start">
            <Avatar
              src={preview || undefined}
              name={form.data.profile.name || user.email_address}
              size="xl"
              className="rounded-full ring-1 ring-border ring-offset-4 ring-offset-background"
            />
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" size="sm" asChild>
                <label htmlFor="account-profile-image" className="cursor-pointer">
                  <Camera className="size-4" />
                  {preview ? copy.change_photo : copy.choose_photo}
                </label>
              </Button>
              {preview && <Button type="button" variant="ghost" size="sm" onClick={() => form.setData('profile', { ...form.data.profile, profile_image: null, remove_profile_image: true })}>
                <Trash2 className="size-4" />
                {copy.remove_photo}
              </Button>}
              <input id="account-profile-image" name="profile[profile_image]" type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" onChange={(event) => selectImage(event.target.files?.[0] || null)} />
            </div>
          </div>
          <p className="mt-3 text-xs leading-5 text-muted-foreground">{copy.photo_hint}</p>
        </div>

        <div className="grid content-start gap-5">
          <div className="space-y-2">
            <Label htmlFor="account-profile-name">{copy.name}</Label>
            <Input id="account-profile-name" value={form.data.profile.name} onChange={(event) => form.setData('profile', { ...form.data.profile, name: event.target.value })} placeholder={copy.name_placeholder} maxLength={255} />
            <p className="text-xs text-muted-foreground">{copy.name_hint}</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="account-profile-email">{copy.email}</Label>
            <Input id="account-profile-email" value={user.email_address} disabled />
            <p className="text-xs text-muted-foreground">{copy.email_hint}</p>
          </div>
          <div>
            <Button type="submit" disabled={form.processing}>
              {form.processing && <Loader2 className="size-4 animate-spin" />}
              {form.processing ? copy.saving : copy.save}
            </Button>
          </div>
        </div>
      </form>
    </CardContent>
  </Card>
}
